import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { AUTH, DASHBOARD } from "@/constants/testIds";

const RAWENS_LOGO =
  "https://customer-assets.emergentagent.com/job_order-production-hub/artifacts/jx48074h_IMG_8811.png";

function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const { login } = useAuth();
  const { t, lang, setLang } = useI18n();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(identifier.trim(), password);
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message || t("error_generic");
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex">
      {/* Left side: industrial visual */}
      <div className="hidden lg:flex flex-1 relative bg-[#0a0a0b] overflow-hidden">
        <div
          className="absolute inset-0 opacity-30 grid-bg"
          style={{ backgroundSize: "48px 48px" }}
        />
        <div className="relative z-10 p-12 flex flex-col justify-between text-white w-full">
          <div className="flex items-center gap-5">
            <img
              data-testid={DASHBOARD.brandLogo}
              src={RAWENS_LOGO}
              alt="Rawen's"
              className="h-40 w-auto bg-white p-3"
            />
            <div className="label-overline text-white/80">PRODUCTION CTRL · RAWEN'S</div>
          </div>
          <div>
            <div className="label-overline text-white/50 mb-4">SYS · 01</div>
            <h1 className="font-display text-7xl xl:text-8xl font-black tracking-tighter leading-[0.85] mb-5">
              {t("app_title")}
            </h1>
            <p className="text-white/60 max-w-md text-sm tracking-wide leading-relaxed uppercase font-mono-tech">{t("app_subtitle")}</p>
            <div className="mt-12 grid grid-cols-3 gap-px bg-white/10 max-w-md">
              {[
                { v: "00", l: t("overdue").toUpperCase() },
                { v: "00", l: t("today").toUpperCase() },
                { v: "00", l: t("upcoming").toUpperCase() },
              ].map((it, i) => (
                <div key={i} className="bg-black p-5">
                  <div className="label-overline text-white/50">{it.l}</div>
                  <div className="font-mono-tech text-4xl font-black mt-1">{it.v}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="font-mono-tech text-xs text-white/40">
            {new Date().toISOString().slice(0, 10)} · v1.0
          </div>
        </div>
      </div>

      {/* Right side: form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 bg-[var(--bg)]">
        <div className="w-full max-w-md">
          <div className="flex justify-between items-center mb-10">
            <img
              src={RAWENS_LOGO}
              alt="Rawen's"
              className="h-32 sm:h-36 w-auto lg:hidden bg-black p-2.5"
            />
            <span className="hidden lg:block" />
            <button
              data-testid={AUTH.langToggle}
              onClick={() => setLang(lang === "es" ? "en" : "es")}
              className="font-mono-tech text-xs border border-[var(--border)] px-3 py-1.5 hover:bg-black hover:text-white transition-colors"
            >
              {lang === "es" ? "ES | en" : "es | EN"}
            </button>
          </div>

          <h2 className="font-display text-4xl font-black tracking-tighter mb-2">
            {t("login_title")}
          </h2>
          <p className="text-[var(--text-secondary)] mb-8">{t("login_subtitle")}</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label-overline block mb-2">{t("email_or_username")}</label>
              <input
                data-testid={AUTH.emailInput}
                type="text"
                autoComplete="username"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
                className="w-full border border-[var(--border)] bg-white px-3 py-2.5 text-base focus:border-[var(--primary)]"
              />
            </div>
            <div>
              <label className="label-overline block mb-2">{t("password")}</label>
              <input
                data-testid={AUTH.passwordInput}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={4}
                className="w-full border border-[var(--border)] bg-white px-3 py-2.5 text-base focus:border-[var(--primary)]"
              />
            </div>

            {error && (
              <div
                data-testid={AUTH.errorMessage}
                className="border border-[var(--alert-overdue)] bg-red-50 text-[var(--alert-overdue)] px-3 py-2 text-sm font-medium"
              >
                {error}
              </div>
            )}

            <button
              data-testid={AUTH.submitBtn}
              type="submit"
              disabled={loading}
              className="w-full bg-[var(--text-primary)] text-white py-3 font-display font-bold tracking-wide hover:bg-[var(--primary)] transition-colors disabled:opacity-50"
            >
              {loading ? "..." : t("enter")}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-[var(--border)] font-mono-tech text-xs text-[var(--text-secondary)]">
            <div>· admin@…  →  {t("admin")}</div>
            <div>· username  →  {t("worker")}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
